/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file matchStatusSlice.js
 * @description Redux slice for managing matchStatus related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the matchStatus slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (matchStatuses = null) => ({
  error: null,
  loading: false,
  matchStatusLanguage: null,
  matchStatuses,
});

/**
 * @function getMatchStatuses
 * @description Async thunk for get match statuses operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getMatchStatuses = createAsyncThunk("matchStatus/loadMatchStatuses", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/matchstatus?originId=${originId}&lineId=${lineId}`);

    return {
      language,
      matchStatuses: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain period details", // serializable (err.response.data)
      name: "Periods List Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant matchStatusSlice
 * @description Redux slice for matchStatus management
 */
const matchStatusSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getMatchStatuses.pending, (state) => {
        state.matchStatuses = null;
        state.error = null;
        state.loading = true;
      })
      .addCase(getMatchStatuses.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getMatchStatuses.fulfilled, (state, action) => {
        state.error = null;
        state.matchStatuses = action.payload.matchStatuses;
        state.matchStatusLanguage = action.payload.language;
        state.loading = false;
      });
  },
  initialState: getInitialState(),
  name: "matchStatus",
  reducers: {},
});
const { reducer } = matchStatusSlice;
export default reducer;
